// Helpers that turn structured field values into the exact QR payload string
// for each QR type. Kept framework-free so it is trivially testable.

// Escape special chars for WiFi payloads: \ ; , : " must be backslash-escaped.
const escapeWifi = (str = '') =>
    String(str).replace(/([\\;,:"])/g, '\\$1')

// Escape special chars for vCard values (mainly ; , \ and newlines).
const escapeVCard = (str = '') =>
    String(str).replace(/([\\;,])/g, '\\$1').replace(/\n/g, '\\n')

export const QR_TYPES = [
    { value: 'url', label: 'Website / URL' },
    { value: 'text', label: 'Plain Text' },
    { value: 'wifi', label: 'WiFi' },
    { value: 'vcard', label: 'Contact (vCard)' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'sms', label: 'SMS' },
    { value: 'geo', label: 'Location (Map)' },
]

// Default field values for every type.
export const defaultFields = {
    url: { url: '' },
    text: { text: '' },
    wifi: { ssid: '', password: '', encryption: 'WPA', hidden: false },
    vcard: { firstName: '', lastName: '', phone: '', email: '', org: '', title: '', url: '' },
    email: { to: '', subject: '', body: '' },
    phone: { number: '' },
    sms: { number: '', message: '' },
    geo: { lat: '', lng: '' },
}

export function buildQrValue(type, f = {}) {
    switch (type) {
        case 'url': {
            const v = (f.url || '').trim()
            if (!v) return ''
            // add https:// if user typed a bare domain (no scheme)
            return /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(v) ? v : `https://${v}`
        }
        case 'text':
            return f.text || ''
        case 'wifi': {
            if (!f.ssid) return ''
            const enc = f.encryption === 'nopass' ? 'nopass' : (f.encryption || 'WPA')
            const parts = [`WIFI:T:${enc}`, `S:${escapeWifi(f.ssid)}`]
            if (enc !== 'nopass') parts.push(`P:${escapeWifi(f.password || '')}`)
            if (f.hidden) parts.push('H:true')
            return parts.join(';') + ';;'
        }
        case 'vcard': {
            if (!f.firstName && !f.lastName && !f.phone && !f.email) return ''
            const name = `${escapeVCard(f.lastName)};${escapeVCard(f.firstName)};;;`
            const full = `${(f.firstName || '').trim()} ${(f.lastName || '').trim()}`.trim()
            const lines = ['BEGIN:VCARD', 'VERSION:3.0', `N:${name}`, `FN:${escapeVCard(full)}`]
            if (f.org) lines.push(`ORG:${escapeVCard(f.org)}`)
            if (f.title) lines.push(`TITLE:${escapeVCard(f.title)}`)
            if (f.phone) lines.push(`TEL;TYPE=CELL:${f.phone}`)
            if (f.email) lines.push(`EMAIL:${f.email}`)
            if (f.url) lines.push(`URL:${f.url}`)
            lines.push('END:VCARD')
            return lines.join('\n')
        }
        case 'email': {
            if (!f.to) return ''
            const params = []
            if (f.subject) params.push(`subject=${encodeURIComponent(f.subject)}`)
            if (f.body) params.push(`body=${encodeURIComponent(f.body)}`)
            return `mailto:${f.to}${params.length ? '?' + params.join('&') : ''}`
        }
        case 'phone':
            return f.number ? `tel:${f.number}` : ''
        case 'sms': {
            if (!f.number) return ''
            return f.message ? `SMSTO:${f.number}:${f.message}` : `SMSTO:${f.number}`
        }
        case 'geo':
            return (f.lat && f.lng) ? `geo:${f.lat},${f.lng}` : ''
        default:
            return ''
    }
}